import React, { useState, useEffect, useMemo } from "react";
import Fuse from "fuse.js";
import { useSelector, useDispatch } from "react-redux";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { JavaIframe } from "component/shared/iframe/java";
import { PageProps } from "shared/page-props";
import * as Router from "react-router-dom";
import * as Icons from "react-feather";
import { FeatherIcon } from "component/shared/feather-icon";
import { RootState } from "redux/store";
import { getMarketsOptimized } from "redux/selector";
import { getUser } from "redux/selector";
import { styles } from "./styles";
import { Table } from "component/shared/table";
import { Market, NewMarket } from "model/market";
import { urls } from "routes/urls";
import { updateMarket, createMarket } from "redux/slice/markets";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { DeleteMarketDialog } from "./delete-dialog";
import MuiAlert from "@mui/material/Alert";
import { NewMarketDialog } from "./new-market-dialog";
import { LinkButton } from "./link-button";
import { fetchMarketReports } from "redux/slice/market-reports";
import * as paths from "routes/paths";
import { CopyID } from "component/shared/copy-id";

interface Props extends
	PageProps, Router.RouteComponentProps,
	WithStyles<typeof styles> {
}

const Component: React.FC<Props> = ({ classes = {}, location, history }) => {
	const dispatch = useDispatch<any>();
	
	// Redux state selectors
	const user = useSelector((state: RootState) => getUser(state));
	const markets = useSelector((state: RootState) => getMarketsOptimized(state));
	const loading = useSelector((state: RootState) => 
		state.markets.loading || state.marketReports.loading || state.marketListings.loading || state.marketReportSubscriptions.loading
	);
	const authLoading = useSelector((state: RootState) => state.authentication.loading);
	const authError = useSelector((state: RootState) => state.authentication.error);

	// Memoized fuse instance
	const fuse = useMemo(() => {
		const options: Fuse.IFuseOptions<Market> = {
			shouldSort: true,
			threshold: 0.5,
			location: 0,
			distance: 100,
			minMatchCharLength: 1,
			keys: ["id", "name"],
		};
		return new Fuse(markets, options);
	}, [markets]);

	// Component state
	const [term, setTerm] = useState("");
	const [deleteMarketDialogIsOpen, setDeleteMarketDialogIsOpen] = useState(false);
	const [marketToDelete, setMarketToDelete] = useState<Market | null>(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [newMarketName, setNewMarketName] = useState("");
	const [snackBarMessage, setSnackBarMessage] = useState<string | undefined>("");
	const [copiedId, setCopiedId] = useState("");

	const hashChange = () => {
		const hash = window.location.hash.substring(1);
		if (hash === "new") {
			setOpenDialog(true);
		}
	};

	useEffect(() => {
		hashChange();
	}, []);

	useEffect(() => {
		hashChange();
	}, [location]);

	const showAddMarketDialog = () => {
		setOpenDialog(true);
	};

	const closeAddMarketDialog = () => {
		setOpenDialog(false);
		window.history.replaceState(null, "", paths.markets);
	};

	const showDeleteMarketDialog = (market: Market) => {
		setMarketToDelete(market);
		setDeleteMarketDialogIsOpen(true);
	};

	const hideDeleteMarketDialog = () => {
		setMarketToDelete(null);
		setDeleteMarketDialogIsOpen(false);
	};

	const deleteMarket = () => {
		if (marketToDelete) {
			dispatch(updateMarket({ 
				market: {
					...marketToDelete,
					inactive: true,
				}
			}));
			hideDeleteMarketDialog();
		}
		openSnackBar("Market Deleted");
	};

	const copyMarket = async (newMarket: Market) => {
		const market: NewMarket = {
			...newMarket,
			name: `${newMarket.name} (copy)`,
		};
		await dispatch(createMarket({ market }));
		dispatch(fetchMarketReports());
		openSnackBar("Market Duplicated");
	};

	const openSnackBar = (message: string) => {
		setSnackBarMessage(undefined);
		setTimeout(() => {
			setSnackBarMessage(message);
		}, 250);
	};

	// Component render
	let activeMarkets = markets.filter((market) => !market.inactive);
	const marketboost = user.permissions.listingReport && user.permissions.marketReport && user.permissions.openHomeReport;
	const title = user.admin ? "Markets" : "Manage Subscribers";
	if (term) {
		activeMarkets = fuse.search(term).map((result) => result.item)
	}

	return (
		<>
			{openDialog && (
				<NewMarketDialog
					user={user}
					closeAddMarketDialog={() => closeAddMarketDialog()}
					helperTextStyle={classes.helperTextStyle}
				/>
			)}
			{marketToDelete && (
				<DeleteMarketDialog
					market={marketToDelete}
					deleteMarket={deleteMarket}
					deleteMarketDialogIsOpen={deleteMarketDialogIsOpen}
					hideDeleteMarketDialog={() => hideDeleteMarketDialog()}
				/>
			)}
			<DashboardLayout
				permitted={user.permissions.markets || (user.type.agent && (user.permissions.modifySubscriptions || user.admin))}
				title={title}
				header={
					<Mui.Grid container justifyContent="flex-start" alignItems="center">
						<Mui.Grid item xs={3}>
							<Mui.Typography variant="h1">
								<FeatherIcon>
									<Icons.MapPin />
								</FeatherIcon>
								{title}
							</Mui.Typography>
						</Mui.Grid>
					</Mui.Grid>
				}
				iframe={!user.admin ? <JavaIframe url="/hotSheets" /> : null}
			>
				{user.admin && (
					<>
						<Mui.Grid container direction="column" spacing={2}>
							<Mui.Grid item>

								<Mui.Grid container justifyContent="space-between" alignItems="center">
									<Mui.Grid item>
										<Mui.InputBase
											placeholder="Search for market"
											onChange={(event) => {
												setTerm(event.target.value);
											}}
											value={term}
											className={classes.contentSearchBar}
											startAdornment={
												<Mui.InputAdornment position="start">
													<FeatherIcon className={classes.contentSearchBarIcon}>
														<Icons.Search />
													</FeatherIcon>
												</Mui.InputAdornment>
											}
										/>
									</Mui.Grid>
									<Mui.Grid item>
										<Mui.Button
											variant="contained"
											color={"primary"}
											onClick={() => showAddMarketDialog()}
										>
											<Mui.Typography>Add Market</Mui.Typography>
										</Mui.Button>
									</Mui.Grid>
								</Mui.Grid>
							</Mui.Grid>
								{loading ? (
									<Mui.Grid item>
										<LoadingIndicator enable />
										{markets.length > 450 && (
											<>
												<Mui.Typography align="center">Still loading {markets.length} markets...</Mui.Typography>
												<Mui.Typography align="center">(This might take a few minutes)</Mui.Typography>
											</>	
										)}
									</Mui.Grid>
								) : (
									<>
										{markets.length > 0 && (
											<Mui.Grid item>
												<Table
													items={activeMarkets.sort((a, b) => a.name.localeCompare(b.name))}
													columns={[
														{
															id: "label",
															hidden: false,
															title: "Name",
															sort: (market1, market2) => {
																return market1.name.toLowerCase() < market2.name.toLowerCase() ? -1 : 1;
															},
															value: (market) => {
																const url = urls.market(market);
																return (
																	<Router.Link 
																		to={{pathname: url, state: {fromMarketName: true}}}
																		className={classes.marketLink}
																	>
																		<Mui.Typography 
																			color={"primary"}
																			className={classes.marketText}
																		>{market.name}</Mui.Typography>
																	</Router.Link>
																);
															}
														},
														{
															id: "id",
															hidden: false,
															title: "ID",
															sort: (market1, market2) => {
																return market2.id.valueOf() - market1.id.valueOf();
															},
															value: (market) => {
																return (
																	<>
																		{market.id && (
																			<CopyID id={market.id} text="Copy Market ID"/>
																		)}
																	</>
																);
															}
														},
														{
															id: "subscribers",
															hidden: marketboost ? false : true,
															title: "Subscribers",
															sort: (market1, market2) => {
																const marketReportSubscriptions1 = market1.marketReportSubscriptions || [];
																const marketReportSubscriptions2 = market2.marketReportSubscriptions || [];
																const count1 = Array.from(new Set(marketReportSubscriptions1.map((item) => item.lead.id))).length;
																const count2 = Array.from(new Set(marketReportSubscriptions2.map((item) => item.lead.id))).length;
																return count2.valueOf() - count1.valueOf();
															},
															value: (market) => {
																const marketReportSubscriptions = market.marketReportSubscriptions || [];
																const count = Array.from(
																	new Set(
																	marketReportSubscriptions
																		.filter((item) => !item.inactive)
																		.map((item) => item.lead.id)
																	)).length;
																const url = urls.marketTab(market, "subscribers");
																const isManual = market && market.criteria ? !!market.criteria.find((criterion) => criterion.name === "ids") : false;
																return (
																	<>
																	{!isManual ? (
																		<Router.Link 
																			to={{pathname: url, state: {fromMarketName: false}}}
																			className={classes.marketLink}
																		>
																		<Mui.Typography color={"primary"}>{count}</Mui.Typography>
																		</Router.Link>
																	): 
																		<Mui.Tooltip 
																			title="Alerts and report pages are not available for Markets using manual listing numbers, date ranges or open-only fields."
																		>
																			<Icons.AlertCircle className={classes.alertCircle}/>
																		</Mui.Tooltip>
																	}
																	</>
																);
															}
														},
														{
															id: "links",
															hidden: false,
															title: "",
															value: (market) => {
																return (
																	<Mui.Box display="flex" justifyContent="flex-end">
																		<LinkButton market={market} marketboost={marketboost} />
																	</Mui.Box>
																);
															}
														},
														{
															id: "duplicate-delete",
															hidden: false,
															showOnHover: true,
															value: (market) => {
																return (
																	<Mui.Grid container justifyContent="flex-end">
																		<Mui.Grid item>
																			<Mui.Tooltip title="Duplicate">
																				<Mui.IconButton
																					onClick={() => copyMarket(market)}
																					>
																					<FeatherIcon>
																						<Icons.Copy />
																					</FeatherIcon>
																				</Mui.IconButton>
																			</Mui.Tooltip>
																		</Mui.Grid>
																		<Mui.Grid item>
																			<Mui.Tooltip title="Delete">
																				<Mui.IconButton
																					onClick={() => showDeleteMarketDialog(market)}
																					>
																					<FeatherIcon>
																						<Icons.Trash />
																					</FeatherIcon>
																				</Mui.IconButton>
																			</Mui.Tooltip>
																		</Mui.Grid>
																	</Mui.Grid>
																)
															}
														}
													]}
												/>
											</Mui.Grid>
										)}
									</>
								)}
							</Mui.Grid>
							<Mui.Snackbar
								open={!!snackBarMessage && !authLoading && !authError}
								message={snackBarMessage}
								autoHideDuration={6000}
								onClose={() => setSnackBarMessage(undefined)}
								action={
									<Mui.IconButton
										size="small"
										aria-label="close"
										color="inherit"
										onClick={() => setSnackBarMessage(undefined)}
									>
										<Icons.X fontSize="small" />
									</Mui.IconButton>
								}
							>
								<MuiAlert severity="info">
									<Mui.Typography>{snackBarMessage}</Mui.Typography>
								</MuiAlert>
							</Mui.Snackbar>
						</>
					)}
				</DashboardLayout>
			</>
		);
};

export const MarketsPage = withStyles(Router.withRouter(Component), styles);